import { useState, useEffect, useCallback, useMemo } from 'react';
import { Maximize2, FileText, Loader2, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PDFViewer } from '@/components/ui/PDFViewer';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { ColDef, RowClassParams } from 'ag-grid-community';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { usePagination } from '@/hooks/usePagination';
import { priceBookItemService, type PriceBookItem } from '@/services';

interface ExtractionInfo {
  extraction_method: string | null;
  extraction_confidence: number | null;
  ai_provider: string | null;
  source_filename: string | null;
}

interface InvoiceSupportingDocsProps {
  invoiceFilePath: string | null;
  ticketFilePath: string | null;
  priceBookId: string | null;
  usedPriceBookItemIds?: string[];
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  focusedPriceBookItemIds?: string[];
  extractionInfo?: ExtractionInfo | null;
  ticketExtractionInfo?: ExtractionInfo | null;
  ticketId?: string | null;
  onDeleteTicket?: () => void;
}

export function InvoiceSupportingDocs({ invoiceFilePath, ticketFilePath, priceBookId, usedPriceBookItemIds, activeTab, onTabChange, focusedPriceBookItemIds, extractionInfo, ticketExtractionInfo, ticketId, onDeleteTicket }: InvoiceSupportingDocsProps) {
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null);
  const [isLoadingInvoicePdf, setIsLoadingInvoicePdf] = useState(false);
  const [ticketPdfUrl, setTicketPdfUrl] = useState<string | null>(null);
  const [isLoadingTicketPdf, setIsLoadingTicketPdf] = useState(false);
  const [showOnlyUsedItems, setShowOnlyUsedItems] = useState(false);
  const [internalTab, setInternalTab] = useState('ticket');
  const currentTab = activeTab ?? internalTab;
  const handleTabChange = onTabChange ?? setInternalTab;
  const hasFocusedSelection = (focusedPriceBookItemIds?.length ?? 0) > 0;

  const {
    pageIndex, pageSize, searchValue, debouncedSearch,
    setSearchValue, setPageIndex, setPageSize,
  } = usePagination({
    defaultPageSize: 25,
    storageKey: 'invoice-pb-items',
    persistToUrl: false,
  });

  const effectiveItemIds = hasFocusedSelection
    ? focusedPriceBookItemIds
    : (showOnlyUsedItems && usedPriceBookItemIds?.length ? usedPriceBookItemIds : undefined);

  const { data: pbData, isLoading: pbLoading, isFetching: pbFetching } = useQuery({
    queryKey: ['invoice-pb-items', priceBookId, pageIndex, pageSize, debouncedSearch, showOnlyUsedItems, focusedPriceBookItemIds],
    queryFn: async () => {
      const result = await priceBookItemService.listByPriceBookPaginated(
        priceBookId!,
        { page: pageIndex, pageSize },
        {
          search: debouncedSearch,
          isActive: true,
          itemIds: effectiveItemIds,
        }
      );
      if (result.error) throw result.error;
      return { items: result.data, totalCount: result.totalCount };
    },
    enabled: !!priceBookId,
    placeholderData: (prev) => prev,
  });

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const priceBookColumnDefs = useMemo<ColDef<PriceBookItem>[]>(() => [
    { headerName: 'Code', field: 'itemcode', flex: 1, minWidth: 80, cellClass: 'font-mono text-xs' },
    { headerName: 'Description', field: 'description', flex: 2, minWidth: 120 },
    { headerName: 'Price', field: 'price', width: 100, type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatCurrency(p.value) : '' },
    { headerName: 'Disc%', field: 'discount', width: 80, type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? `${p.value}%` : '' },
    { headerName: 'Net', colId: 'net', width: 100, type: 'rightAligned',
      valueGetter: (p) => p.data ? p.data.price * (1 - p.data.discount / 100) : null,
      valueFormatter: (p) => p.value != null ? formatCurrency(p.value) : '',
      cellClass: 'font-medium' },
  ], []);

  const usedIdsSet = useMemo(() => new Set(usedPriceBookItemIds ?? []), [usedPriceBookItemIds]);
  const focusedIdsSet = useMemo(() => new Set(focusedPriceBookItemIds ?? []), [focusedPriceBookItemIds]);

  // Reset pagination when focused selection changes
  useEffect(() => { setPageIndex(0); }, [focusedPriceBookItemIds]);

  const getRowClass = useCallback((params: RowClassParams<PriceBookItem>) => {
    if (params.data && focusedIdsSet.has(params.data.id)) return 'ag-row-pricebook-focused';
    if (params.data && usedIdsSet.has(params.data.id)) return 'ag-row-pricebook-used';
    return undefined;
  }, [focusedIdsSet, usedIdsSet]);

  const priceBookToolbarActions = (
    <div className="flex items-center gap-2">
      {hasFocusedSelection ? (
        <span className="text-xs text-primary font-medium whitespace-nowrap">
          Showing {focusedPriceBookItemIds!.length} selected item{focusedPriceBookItemIds!.length !== 1 ? 's' : ''}
        </span>
      ) : (
        <>
          <Checkbox id="show-used-only" checked={showOnlyUsedItems}
            onCheckedChange={(checked) => { setShowOnlyUsedItems(checked === true); setPageIndex(0); }} />
          <label htmlFor="show-used-only" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
            Used items only
          </label>
        </>
      )}
    </div>
  );

  // Fetch invoice PDF
  useEffect(() => {
    let currentObjectUrl: string | null = null;

    const fetchPdf = async () => {
      if (!invoiceFilePath) {
        setInvoicePdfUrl(null);
        return;
      }

      setIsLoadingInvoicePdf(true);
      try {
        const { data, error } = await supabase.storage
          .from('invoice-files')
          .download(invoiceFilePath);

        if (error) throw error;

        if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = URL.createObjectURL(data);
        setInvoicePdfUrl(currentObjectUrl);
      } catch (err) {
        console.error('Error fetching invoice PDF:', err);
        setInvoicePdfUrl(null);
      } finally {
        setIsLoadingInvoicePdf(false);
      }
    };

    fetchPdf();

    return () => {
      if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
    };
  }, [invoiceFilePath]);

  // Fetch ticket PDF
  useEffect(() => {
    let currentObjectUrl: string | null = null;

    const fetchPdf = async () => {
      if (!ticketFilePath) {
        setTicketPdfUrl(null);
        return;
      }

      setIsLoadingTicketPdf(true);
      try {
        const { data, error } = await supabase.storage
          .from('ticket-files')
          .download(ticketFilePath);

        if (error) throw error;

        if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = URL.createObjectURL(data);
        setTicketPdfUrl(currentObjectUrl);
      } catch (err) {
        console.error('Error fetching ticket PDF:', err);
        setTicketPdfUrl(null);
      } finally {
        setIsLoadingTicketPdf(false);
      }
    };

    fetchPdf();

    return () => {
      if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
    };
  }, [ticketFilePath]);

  const handleOpenInvoiceInNewTab = useCallback(() => {
    if (!invoiceFilePath) return;
    const url = `/pdf?bucket=invoice-files&path=${encodeURIComponent(invoiceFilePath)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [invoiceFilePath]);

  const handleOpenTicketInNewTab = useCallback(() => {
    if (!ticketFilePath) return;
    const url = `/pdf?bucket=ticket-files&path=${encodeURIComponent(ticketFilePath)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [ticketFilePath]);

  return (
    <div className="w-full flex flex-col">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        Supporting Documents
      </h2>

      <Tabs value={currentTab} onValueChange={handleTabChange} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-3">
          <TabsTrigger value="ticket" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 text-sm">
            Field Ticket
          </TabsTrigger>
          <TabsTrigger value="invoice" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 text-sm">
            Invoice
          </TabsTrigger>
          <TabsTrigger value="pricebook" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 text-sm">
            Price Book
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoice" className="mt-3 data-[state=inactive]:hidden">
          <div className="bg-card border border-border rounded-lg overflow-hidden flex flex-col min-h-[500px]">
            {isLoadingInvoicePdf ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : invoicePdfUrl ? (
              <>
                <PDFViewer url={invoicePdfUrl} className="flex-1 min-h-[450px]" />
                <div className="flex items-center justify-center gap-2 py-2 border-t border-border bg-muted/30">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={handleOpenInvoiceInNewTab}
                    title="Open in New Tab"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                <FileText className="w-12 h-12 mb-2" />
                <p className="text-sm">No invoice PDF available</p>
              </div>
            )}
          </div>
          {extractionInfo && (
            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Extraction Info</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Source File</p>
                  <p className="text-sm text-foreground">{extractionInfo.source_filename || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Extraction Method</p>
                  <p className="text-sm text-foreground">{extractionInfo.extraction_method || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Model</p>
                  <p className="text-sm text-foreground">{extractionInfo.ai_provider || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Confidence</p>
                  <p className="text-sm text-foreground">
                    {extractionInfo.extraction_confidence != null
                      ? `${extractionInfo.extraction_confidence.toFixed(0)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ticket" className="mt-3 data-[state=inactive]:hidden">
          <div className="bg-card border border-border rounded-lg overflow-hidden flex flex-col min-h-[500px]">
            {isLoadingTicketPdf ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : ticketPdfUrl ? (
              <>
                <PDFViewer url={ticketPdfUrl} className="flex-1 min-h-[450px]" />
                <div className="flex items-center justify-center gap-2 py-2 border-t border-border bg-muted/30">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={handleOpenTicketInNewTab}
                    title="Open in New Tab"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                  {ticketId && onDeleteTicket && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          title="Delete Field Ticket"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Field Ticket</AlertDialogTitle>
                          <AlertDialogDescription>
                            Delete this field ticket and all associated files? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={onDeleteTicket} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                <FileText className="w-12 h-12 mb-2" />
                <p className="text-sm">No field ticket PDF available</p>
              </div>
            )}
          </div>
          {ticketExtractionInfo && (
            <div className="mt-4 bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Extraction Info</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Source File</p>
                  <p className="text-sm text-foreground">{ticketExtractionInfo.source_filename || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Extraction Method</p>
                  <p className="text-sm text-foreground">{ticketExtractionInfo.extraction_method || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Model</p>
                  <p className="text-sm text-foreground">{ticketExtractionInfo.ai_provider || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Confidence</p>
                  <p className="text-sm text-foreground">
                    {ticketExtractionInfo.extraction_confidence != null
                      ? `${ticketExtractionInfo.extraction_confidence.toFixed(0)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="pricebook" className="mt-3 data-[state=inactive]:hidden">
          {priceBookId ? (
            <AGDataGrid<PriceBookItem>
              rowData={pbData?.items ?? []}
              columnDefs={priceBookColumnDefs}
              totalCount={pbData?.totalCount ?? 0}
              pageIndex={pageIndex}
              pageSize={pageSize}
              searchValue={searchValue}
              searchPlaceholder="Search items..."
              isLoading={pbLoading}
              isFetching={pbFetching}
              onSearchChange={setSearchValue}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              height="calc(100vh - 380px)"
              toolbarActions={priceBookToolbarActions}
              getRowClass={getRowClass}
              rowSelection={false}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No price book associated with this invoice.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
